import type { Team } from '../types';
import type { PlayerCard } from '../types/playerCard';

/**
 * Generate print HTML for roster
 */
export function generateRosterPrintHTML(
  team: Team,
  playerCards: PlayerCard[]
): string {
  // Sort players by number (default) or last name
  const sortedCards = [...playerCards].sort((a, b) => {
    const numA = a.player?.number ?? 999;
    const numB = b.player?.number ?? 999;
    return numA - numB;
  });

  // Generate table rows
  const tableRows = sortedCards
    .map((card) => {
      const player = card.player;
      if (!player) return '';

      const name = `${player.first_name || ''} ${player.last_name || ''}`.trim();
      const number = player.number || '';
      const positions = card.eligible_positions?.join(', ') || '--';
      const ovrRating = (card.ovr_rating !== undefined && card.ovr_rating !== null) ? card.ovr_rating.toString() : '--';
      const height = player.height || '--';
      const weight = player.weight || '--';
      const injuryStatus = player.injury_status || 'H';
      const playParticipation = (player as any).playParticipation || (player as any).play_participation;
      const hasMPR = playParticipation?.hasMPR || false;
      const minPlays = playParticipation?.minimumRequiredPlays;
      const mprPlays = hasMPR && minPlays !== undefined && minPlays !== null ? minPlays.toString() : '--';

      // Get injury status display
      const getInjuryDisplay = (status: string) => {
        switch (status) {
          case 'H': return '';
          case 'Q': return 'Q';
          case 'D': return 'D';
          case 'O': return 'OUT';
          default: return '';
        }
      };

      const injuryDisplay = getInjuryDisplay(injuryStatus);

      return `
        <tr>
          <td>${number || '--'}</td>
          <td>${name}</td>
          <td>${positions}</td>
          <td>${ovrRating}</td>
          <td>${height}</td>
          <td>${weight}</td>
          <td>${injuryDisplay}</td>
          <td>${hasMPR ? mprPlays : '--'}</td>
        </tr>
      `;
    })
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${team.name} - Roster</title>
        <style>
          @page {
            size: letter;
            margin: 0.5in;
          }
          
          @media print {
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
              font-size: 10pt;
              line-height: 1.3;
            }
            
            .print-container {
              width: 100%;
            }
            
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            
            .header h1 {
              margin: 0 0 5px 0;
              font-size: 18pt;
              font-weight: bold;
            }
            
            .team-info {
              font-size: 9pt;
              color: #666;
            }
            
            .team-info span {
              margin: 0 15px;
            }
            
            .roster-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            
            .roster-table th {
              background-color: #f0f0f0;
              border: 1px solid #000;
              padding: 8px 6px;
              text-align: left;
              font-weight: bold;
              font-size: 9pt;
            }
            
            .roster-table td {
              border: 1px solid #000;
              padding: 6px;
              font-size: 9pt;
            }
            
            .roster-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            
            .number-col {
              width: 50px;
              text-align: center;
            }
            
            .name-col {
              width: 180px;
            }
            
            .positions-col {
              width: 150px;
            }
            
            .rating-col {
              width: 60px;
              text-align: center;
            }
            
            .height-col {
              width: 80px;
              text-align: center;
            }
            
            .weight-col {
              width: 80px;
              text-align: center;
            }
            
            .status-col {
              width: 60px;
              text-align: center;
            }
            
            .mpr-col {
              width: 70px;
              text-align: center;
            }
          }
          
          @media screen {
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            
            .roster-table {
              font-size: 12px;
            }
            
            .roster-table th,
            .roster-table td {
              padding: 8px;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <div class="header">
            <h1>${team.name} - Roster</h1>
            <div class="team-info">
              <span>Sport: ${team.sport}</span>
              ${team.offensiveScheme ? `<span>Offensive Scheme: ${team.offensiveScheme}</span>` : ''}
              ${team.defensiveScheme ? `<span>Defensive Scheme: ${team.defensiveScheme}</span>` : ''}
            </div>
          </div>
          
          <table class="roster-table">
            <thead>
              <tr>
                <th class="number-col">#</th>
                <th class="name-col">Name</th>
                <th class="positions-col">Positions</th>
                <th class="rating-col">OVR</th>
                <th class="height-col">Height</th>
                <th class="weight-col">Weight</th>
                <th class="status-col">Status</th>
                <th class="mpr-col">MPR</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows || '<tr><td colspan="8" style="text-align: center; padding: 20px;">No players found</td></tr>'}
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `;
}

/**
 * Print roster
 */
export async function printRoster(
  team: Team,
  playerCards: PlayerCard[]
): Promise<void> {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('Failed to open print window');
    return;
  }

  const html = generateRosterPrintHTML(team, playerCards);
  
  printWindow.document.write(html);
  printWindow.document.close();
  
  // Wait a moment for content to load, then print
  setTimeout(() => {
    printWindow.print();
  }, 250);
}